import { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS } from '../config';
import { isPlaceholderColor } from '../utils/placeholderColor';

const FilterSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-border pb-4 mb-4">
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between py-2 hover:text-primary transition-colors"
        >
            <span className="font-semibold text-foreground">{title}</span>
            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xl`}></i>
        </button>
        {isExpanded && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto scrollbar-thin">
                {children}
            </div>
        )}
    </div>
);

const CheckboxOption = ({ label, count, checked, onChange }) => (
    <label className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors">
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer flex-shrink-0"
        />
        <span className="text-sm text-foreground flex-1 truncate">{label}</span>
        {count !== undefined && (
            <span className="text-xs text-muted-foreground/60 flex-shrink-0">({count})</span>
        )}
    </label>
);

const LoadingSkeleton = () => (
    <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-5 bg-muted rounded animate-pulse"></div>)}
    </div>
);

export default function FilterPanel({ filters, onFilterChange, baseFilters = {} }) {
    const [availableFilters, setAvailableFilters] = useState(null);
    const [loadingFilters, setLoadingFilters] = useState(true);
    const [expandedSections, setExpandedSections] = useState({
        brand: true,
        category: true,
        color: false,
        price: true,
    });
    const [localMinPrice, setLocalMinPrice] = useState(filters.minPrice || '');
    const [localMaxPrice, setLocalMaxPrice] = useState(filters.maxPrice || '');

    // Fetch available filters from API
    const fetchFilters = useCallback(async () => {
        setLoadingFilters(true);
        try {
            const params = new URLSearchParams();

            // Only pass base filters (isNewArrival, isBestSeller) and search.
            // Do NOT pass brand/category/color/attribute selections —
            // otherwise selected options hide other options from the list.
            Object.entries(baseFilters).forEach(([key, val]) => {
                if (val) params.set(key, val);
            });
            if (filters.search) params.set('search', filters.search);
            if (filters.showAll === 'true') params.set('showAll', 'true');

            const res = await fetch(`${ENDPOINTS.PRODUCTS.FILTERS}?${params.toString()}`);
            const data = await res.json();

            if (data?.isOk && data?.data) {
                setAvailableFilters(data.data);
            }
        } catch (err) {
            console.error('Failed to load filters:', err);
        } finally {
            setLoadingFilters(false);
        }
    }, [baseFilters.isNewArrival, baseFilters.isBestSeller, filters.search, filters.showAll]);

    useEffect(() => {
        fetchFilters();
    }, [fetchFilters]);

    // Sync price inputs with URL
    useEffect(() => {
        setLocalMinPrice(filters.minPrice || '');
        setLocalMaxPrice(filters.maxPrice || '');
    }, [filters.minPrice, filters.maxPrice]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Multi-select toggle helper
    const toggleMultiSelect = (currentValue, slug) => {
        const current = currentValue ? currentValue.split(',').filter(Boolean) : [];
        const idx = current.indexOf(slug);
        if (idx >= 0) {
            current.splice(idx, 1);
        } else {
            current.push(slug);
        }
        return current.join(',') || '';
    };

    const handleBrandToggle = (brandSlug) => {
        onFilterChange({ brand: toggleMultiSelect(filters.brand, brandSlug) });
    };

    const handleCategoryToggle = (catSlug) => {
        onFilterChange({ category: toggleMultiSelect(filters.category, catSlug) });
    };

    const handleColorToggle = (colorSlug) => {
        onFilterChange({ color: toggleMultiSelect(filters.color, colorSlug) });
    };

    const handleAttributeToggle = (attrSlug, valueSlug) => {
        onFilterChange({ [attrSlug]: toggleMultiSelect(filters[attrSlug], valueSlug) });
    };

    const handlePriceApply = () => {
        onFilterChange({ minPrice: localMinPrice || '', maxPrice: localMaxPrice || '' });
    };

    const clearAllFilters = () => {
        const resetObj = {
            brand: '',
            category: '',
            color: '',
            search: '',
            minPrice: '',
            maxPrice: '',
            onSale: '',
            sort: 'newest',
        };
        // Also clear any dynamic attribute filters
        if (availableFilters?.attributes) {
            availableFilters.attributes.forEach(attr => {
                resetObj[attr.attributeSlug] = '';
            });
        }
        onFilterChange(resetObj);
    };

    // Check if any filter is active
    const hasActiveFilters = filters.brand || filters.category || filters.color ||
        filters.search || filters.minPrice || filters.maxPrice || filters.onSale ||
        (availableFilters?.attributes || []).some(attr => filters[attr.attributeSlug]);

    const isChecked = (filterValue, slug) => {
        if (!filterValue) return false;
        return filterValue.split(',').includes(slug);
    };

    const priceRange = availableFilters?.priceRange || { min: 0, max: 500000 };

    return (
        <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">Filters</h2>
                {hasActiveFilters && (
                    <button
                        onClick={clearAllFilters}
                        className="text-sm text-primary hover:text-primary/85 font-medium transition-colors whitespace-nowrap"
                    >
                        Clear All
                    </button>
                )}
            </div>

            {/* On Sale Toggle */}
            <div className="border-b border-border pb-4 mb-4">
                <label className="flex items-center justify-between cursor-pointer">
                    <span className="font-semibold text-foreground">On Sale</span>
                    <div className="relative">
                        <input
                            type="checkbox"
                            checked={filters.onSale === 'true'}
                            onChange={(e) => onFilterChange({ onSale: e.target.checked ? 'true' : '' })}
                            className="sr-only peer"
                        />
                        <div className="w-10 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors"></div>
                        <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform peer-checked:translate-x-5"></div>
                    </div>
                </label>
            </div>

            {/* Category */}
            <FilterSection
                title="Category"
                isExpanded={expandedSections.category}
                onToggle={() => toggleSection('category')}
            >
                {loadingFilters ? <LoadingSkeleton /> :
                    !availableFilters?.categories?.length ? (
                        <p className="text-sm text-muted-foreground/60">No categories available</p>
                    ) : (
                        availableFilters.categories.map(cat => (
                            <CheckboxOption
                                key={cat._id}
                                label={cat.name}
                                count={cat.count}
                                checked={isChecked(filters.category, cat.slug)}
                                onChange={() => handleCategoryToggle(cat.slug)}
                            />
                        ))
                    )
                }
            </FilterSection>

            {/* Brand */}
            <FilterSection
                title="Brand"
                isExpanded={expandedSections.brand}
                onToggle={() => toggleSection('brand')}
            >
                {loadingFilters ? <LoadingSkeleton /> :
                    !availableFilters?.brands?.length ? (
                        <p className="text-sm text-muted-foreground/60">No brands available</p>
                    ) : (
                        availableFilters.brands.map(b => (
                            <CheckboxOption
                                key={b._id}
                                label={b.name}
                                count={b.count}
                                checked={isChecked(filters.brand, b.slug)}
                                onChange={() => handleBrandToggle(b.slug)}
                            />
                        ))
                    )
                }
            </FilterSection>

            {/* Color */}
            {availableFilters?.colors?.filter((c) => !isPlaceholderColor(c)).length > 0 && (
                <FilterSection
                    title="Color"
                    isExpanded={expandedSections.color}
                    onToggle={() => toggleSection('color')}
                >
                    {availableFilters.colors.filter((c) => !isPlaceholderColor(c)).map(c => (
                        <label key={c._id} className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors">
                            <input
                                type="checkbox"
                                checked={isChecked(filters.color, c.slug)}
                                onChange={() => handleColorToggle(c.slug)}
                                className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                            />
                            {c.hexCode && (
                                <span
                                    className="w-4 h-4 rounded-full border border-border flex-shrink-0"
                                    style={{ backgroundColor: c.hexCode }}
                                ></span>
                            )}
                            <span className="text-sm text-foreground flex-1 truncate">{c.name}</span>
                            <span className="text-xs text-muted-foreground/60 flex-shrink-0">({c.count})</span>
                        </label>
                    ))}
                </FilterSection>
            )}

            {/* Dynamic Attribute Sections */}
            {availableFilters?.attributes?.map(attr => (
                <FilterSection
                    key={attr.attributeId}
                    title={attr.attributeName}
                    isExpanded={expandedSections[attr.attributeSlug]}
                    onToggle={() => toggleSection(attr.attributeSlug)}
                >
                    {attr.values.map(val => (
                        <CheckboxOption
                            key={val.valueId}
                            label={val.valueName}
                            count={val.count}
                            checked={isChecked(filters[attr.attributeSlug], val.valueSlug)}
                            onChange={() => handleAttributeToggle(attr.attributeSlug, val.valueSlug)}
                        />
                    ))}
                </FilterSection>
            ))}

            {/* Price Range */}
            <FilterSection
                title="Price Range"
                isExpanded={expandedSections.price}
                onToggle={() => toggleSection('price')}
            >
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            value={localMinPrice}
                            onChange={(e) => setLocalMinPrice(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none focus:border-primary"
                            placeholder={`Min (${priceRange.min?.toLocaleString() || 0})`}
                            min="0"
                        />
                        <span className="text-muted-foreground">-</span>
                        <input
                            type="number"
                            value={localMaxPrice}
                            onChange={(e) => setLocalMaxPrice(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none focus:border-primary"
                            placeholder={`Max (${priceRange.max?.toLocaleString() || 0})`}
                            min="0"
                        />
                    </div>
                    <button
                        onClick={handlePriceApply}
                        className="w-full py-2 bg-primary text-white text-sm rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                    >
                        Apply Price
                    </button>
                </div>
            </FilterSection>
        </div>
    );
}
